import { Component, DestroyRef, inject, input, signal } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { GridForm, TabForm, TabFormModel } from '@sites/entity-signal-form';
import { transactMany } from '@sites/entity-signal-form';
import { transactThrottle } from '@sites/entity-signal-form';

import { CSS_RESTRICTIONS } from '@common/constants';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';

import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpAdvancedRadiusSettingsComponent } from '@components/sp-advanced-radius-settings/sp-advanced-radius-settings.component';
import { SpSwitchComponent } from '@ui/sp-switch';

import { SettingsBreadcrumbsComponent } from '@web/shared/components/web-settings/settings-breadcrumbs/settings-breadcrumbs.component';
import { WebColorPickerV2Component } from '@web/shared/components/web-color-picker-v2/web-color-picker-v2.component';

import { SettingsFooterComponent } from '@web/shared/components/web-settings/settings-footer/settings-footer.component';

import { GridsService } from '@web/store/page-structure/grids/grids.service';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WebGridFormService } from '@web/core/services/web-grid-form.service';

import { AbstractSignalSettingsComponent } from '../../abstract-base-settings/abstract-signal-settings.component';

@Component({
    selector: 'web-tab-appearance-settings',
    templateUrl: './tab-appearance-settings.component.html',
    styleUrls: ['./tab-appearance-settings.component.less'],
    imports: [
        A11yModule,
        TooltipModule,
        TranslatePipe,
        SpInputNumComponent,
        SpAdvancedRadiusSettingsComponent,
        SpSwitchComponent,
        WebColorPickerV2Component,
        SettingsBreadcrumbsComponent,
        SettingsFooterComponent,
    ],
})
export class TabAppearanceSettingsComponent extends AbstractSignalSettingsComponent<TabFormModel> {
    public readonly form = input.required<TabForm>();

    private readonly gridsFacade = inject(GridsFacade);
    private readonly gridsService = inject(GridsService);
    private readonly gridFormService = inject(WebGridFormService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly throttledUpdate = transactThrottle(() => this.form(), 300);

    public childrenGridForm: GridForm | null = null;
    private childrenGridForms: GridForm[] = [];

    public hasChildrenGridForm = signal(false);
    public useIndividualHeaderRadiusSettings = signal(false);

    public readonly CSS_RESTRICTIONS = CSS_RESTRICTIONS;

    protected readFormValue(): TabFormModel {
        return this.form().value();
    }

    protected writeFormValue(value: TabFormModel): void {
        this.form().setValue(value);
    }

    public override async initSubscriptions(): Promise<void> {
        const radius = this.form().headerBorderRadiusValue().value();
        this.useIndividualHeaderRadiusSettings.set(
            !(
                radius.topLeft === radius.topRight &&
                radius.topLeft === radius.bottomLeft &&
                radius.topLeft === radius.bottomRight
            )
        );

        const children = this.form().children().value() as string[];
        const firstChildId = children[0];
        if (!firstChildId) {
            return;
        }

        const grid = await firstValueFrom(this.gridsFacade.byId$(firstChildId));
        if (!grid) {
            return;
        }

        const layouts = this.gridsService.getVariantLayout(grid.props.variant);
        if (layouts[0].length === 1) {
            return;
        }

        this.childrenGridForm = this.gridFormService.getOrCreateScoped(firstChildId, this.destroyRef);
        this.childrenGridForms = children.map((id) => this.gridFormService.getOrCreateScoped(id, this.destroyRef));
        this.hasChildrenGridForm.set(true);
    }

    public toggleUseIndividualHeaderRadiusSettings(): void {
        this.useIndividualHeaderRadiusSettings.update((v) => !v);
        if (!this.useIndividualHeaderRadiusSettings()) {
            const value = this.form().headerBorderRadiusValue().value().topLeft ?? 0;
            this.form().setAllHeaderBorderRadius(value);
        }
    }

    public setHeaderTextColor(payload: ColorPickerEventPayload): void {
        this.throttledUpdate(() => {
            this.form().setHeaderTextColor(payload);
        });
    }

    public setStructureGap(value: number): void {
        transactMany(
            this.childrenGridForms.map((f) => f),
            () => {
                this.childrenGridForms.forEach((form) => {
                    form.setGapX(value);
                    form.setGapY(value);
                });
            }
        );
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get PADDING_MIN(): number {
        return CSS_RESTRICTIONS.PADDING_MIN;
    }

    public get PADDING_MAX(): number {
        return CSS_RESTRICTIONS.PADDING_MAX;
    }

    public get FONT_SIZE_MIN(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MIN;
    }

    public get FONT_SIZE_MAX(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MAX;
    }
}
